import httpApi from '@/utils/api'
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
  strict: true,

  /**
   * List of all state variables
   */
  state: {
    // Initialize with token from storage
    token: localStorage.getItem('user-token') || '',

    // Track auth status to show login button and loading animation
    authStatus: '',

    // Save auth error here to show the error to user
    authError: '',

    // Save user's name and ID, after login
    user: {},

    isGift: false,

    subscribe: {
      planID: 0,
      email: '',
      name: '',
      phone: '',

      toEmail: '',
      toName: '',
      toPhone: '',
      deliveryCity: '0',
      // If other city, save the city title, to fill in delivery details later
      city: ''
    },

    // List all errors in this array
    errors: [],

    // Props for status message component. Used to show success / errors of API actions
    snackbarStatus: {
      active: false,
      color: '',
      message: ''
    },

    // Used for file update progressbar
    uploadPercentage: 0
  },

  /**
   * List of all getters
   */
  getters: {
    // Getter for state variables above
    token: state => state.token,
    authStatus: state => state.authStatus,
    authError: state => state.authError,
    user: state => state.user,
    isGift: state => state.isGift,
    subscribe: state => state.subscribe,
    errors: state => state.errors,
    snackbarStatus: state => state.snackbarStatus,
    uploadPercentage: state => state.uploadPercentage,

    // Check if token is set and return auth status
    isAuthenticated: state => !!state.token
  },

  /**
   * List of all mutations -
   * Mutations can change state data, but
   * cannot be used for async operations like API calls.
   * Mutations are committed with commit(MUTATION_NAME, payload)
   * Mutations receive (state, payload)
   */
  mutations: {
    // On auth request, set the status to loading, to hide login button + show loading animations
    AUTH_REQUEST: (state) => {
      state.authStatus = 'loading'
    },

    // On auth success, set status, token and user data
    AUTH_SUCCESS: (state, resp) => {
      state.authStatus = 'success'
      state.token = resp.data.token
      state.user = {
        name: resp.data.fullName,
        id: resp.data.userID
      }
    },

    // On auth error, set status, token and user data
    AUTH_ERROR: (state, err) => {
      state.authStatus = 'error'
      if (typeof err === 'undefined') {
        state.authError = 'Unknown / network error'
      } else {
        state.authError = err.message
      }
    },

    // On getting name, save it to state
    AUTH_GET_NAME: (state, resp) => {
      state.user = {
        name: resp.data.fullName,
        id: resp.data.userID
      }
    },

    // Save user's plan and initial details to state
    SAVE_USER_SUBSCRIPTION_SELECTION: (state, payload) => {
      state.subscribe = {
        planID: payload.planID,
        email: payload.email,
        name: payload.name,
        phone: payload.phone,
        toEmail: payload.toEmail,
        toName: payload.toName,
        toPhone: payload.toPhone,
        deliveryCity: payload.deliveryCity,
        city: payload.city
      }
    },

    // Set gift active
    SET_GIFT: (state, payload) => {
      state.isGift = payload
    },

    // On user logout, clear token and user data
    AUTH_LOGOUT: (state) => {
      state.token = ''
      state.user = {}
    },

    // Show the snackbar with message
    SHOW_STATUS_MESSAGE: (state, payload) => {
      state.snackbarStatus = {
        active: true,
        color: payload.color,
        message: payload.message
      }
    },

    // Hide the snackbar
    HIDE_STATUS_MESSAGE: (state) => {
      state.snackbarStatus.active = false
    },

    UPLOAD_PROGRESS_UPDATE: (state, payload) => {
      state.uploadPercentage = payload
    }
  },

  /**
   * List of all actions -
   * Component methods call an action, which makes API calls and
   * calls mutations to change state data.
   * Actions are dispatched with dispatch(ACTION_NAME, payload)
   * Actions receive ({commit, dispatch}, payload)
   */
  actions: {
    // Component method dispatched this login action
    AUTH_REQUEST: ({ commit, dispatch }, user) => {
      // Create a Promise instance, because of async API calls
      return new Promise((resolve, reject) => {
        // First commit mutation to update the authStatus
        commit('AUTH_REQUEST')

        // Make the API call
        httpApi.callWithKey({
          url: 'user/login',
          data: user,
          method: 'post'
        })
          // On success, save the token to localStorage and commit mutation
          .then(resp => {
            localStorage.setItem('user-token', resp.data.token)
            commit('AUTH_SUCCESS', resp)
            resolve(resp)
          })
          // On error, remove any previously saved token and commit mutation
          .catch(err => {
            commit('AUTH_ERROR', err)
            localStorage.removeItem('user-token')
            reject(err)
          })
      })
    },

    // Get user's name when user accesses app by token
    AUTH_GET_NAME: ({ commit, dispatch }) => {
      // Create a Promise instance, because of async API calls
      return new Promise((resolve, reject) => {
        // Make the API call
        httpApi.callWithToken({
          url: 'user/name',
          method: 'get'
        })
          // On success, commit mutation
          .then(resp => {
            commit('AUTH_GET_NAME', resp)
            resolve(resp)
          })
          // On error, reject
          .catch(err => {
            reject(err)
          })
      })
    },

    // Component method dispatched this logout action
    AUTH_LOGOUT: ({ commit, dispatch }) => {
      return new Promise((resolve, reject) => {
        // Make the API call
        httpApi.callWithToken({
          url: 'user/logout',
          method: 'get'
        })
          // On success, remove the token from localStorage and commit mutation
          .then(resp => {
            // Commit mutation to clear state data
            commit('AUTH_LOGOUT')
            localStorage.removeItem('user-token')
            resolve(resp)
          })
          // On error, only logged out locally. API may still have an active session
          .catch(err => {
            commit('AUTH_LOGOUT')
            localStorage.removeItem('user-token')
            reject(err)
          })
      })
    },

    // Show the snackbar with status message and timeout after 6 seconds.
    // Not using snackbar's default timeout functionality, because
    // it doesn't emit a close event, which we can use to change the active state
    SHOW_STATUS_MESSAGE: ({ commit, dispatch }, payload) => {
      commit('SHOW_STATUS_MESSAGE', payload)
      setTimeout(() => {
        commit('HIDE_STATUS_MESSAGE')
      }, 6000)
    }
  }
})
